import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const MODAL_BASE_Z = 1300;

interface ModalStackContextValue {
    openModal: (id: string) => void;
    closeModal: (id: string) => void;
    isOpen: (id: string) => boolean;
    zIndexFor: (id: string) => number;
}

const ModalStackContext = createContext<ModalStackContextValue | null>(null);

export const ModalStackProvider: React.FC = ({ children }) => {
    const [stack, setStack] = useState<string[]>([]);

    const openModal = useCallback((id: string) => {
        setStack(prev => {
            if (process.env.NODE_ENV !== 'production' && prev.includes(id)) {
                console.warn(`ModalStackContext: duplicate modal id "${id}" registered`);
            }
            return prev.includes(id) ? prev : [...prev, id];
        });
    }, []);

    const closeModal = useCallback((id: string) => {
        setStack(prev => prev.filter(item => item !== id));
    }, []);

    const isOpen = useCallback((id: string) => stack.includes(id), [stack]);

    const zIndexFor = useCallback(
        (id: string) => {
            const index = stack.indexOf(id);
            return index === -1 ? MODAL_BASE_Z : MODAL_BASE_Z + 10 * index;
        },
        [stack]
    );

    const value = useMemo<ModalStackContextValue>(() => ({ openModal, closeModal, isOpen, zIndexFor }), [
        openModal,
        closeModal,
        isOpen,
        zIndexFor,
    ]);

    return <ModalStackContext.Provider value={value}>{children}</ModalStackContext.Provider>;
};

export const useModalStack = (): ModalStackContextValue => {
    const ctx = useContext(ModalStackContext);
    if (ctx === null) {
        throw new Error('useModalStack must be used within a ModalStackProvider');
    }
    return ctx;
};
